/**
 * Image Color Extraction Engine
 * 
 * Pure utility module — no React dependencies, no UI imports.
 * Ported from Brand Harmony Palette's colorUtils.ts with adaptations
 * for the Agency+ Color type system.
 * 
 * Features:
 *  - K-means pixel clustering for dominant color extraction
 *  - WCAG 2.1 contrast ratio & compliance level
 *  - Accessible shade/tint generation with W/B contrast scores
 */

// ─── Core Types ──────────────────────────────────────────────────────────────

export interface RGB { r: number; g: number; b: number }
export interface HSL { h: number; s: number; l: number }

export interface ExtractedColorInfo {
  hex: string;
  rgb: RGB;
  hsl: HSL;
  name: string;
  population: number;   // % of image this cluster represents
  light: string;        // lighter tint hex
  dark: string;         // darker shade hex
}

export interface PaletteResult {
  primary: ExtractedColorInfo[];
  accent: ExtractedColorInfo[];
  neutral: ExtractedColorInfo[];
}

export interface AccessibleShadeInfo {
  hex: string;
  label: string;
  contrastOnWhite: number;
  contrastOnBlack: number;
  wcagWhite: WcagLevel;
  wcagBlack: WcagLevel;
}

export type WcagLevel = 'AAA' | 'AA' | 'AA Large' | 'Fail';

// ─── Color Conversions ──────────────────────────────────────────────────────

export function rgbToHex({ r, g, b }: RGB): string {
  return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
}

export function hexToRgbValues(hex: string): RGB {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function rgbToHslValues({ r, g, b }: RGB): HSL {
  const r1 = r / 255, g1 = g / 255, b1 = b / 255;
  const max = Math.max(r1, g1, b1), min = Math.min(r1, g1, b1);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r1) h = ((g1 - b1) / d + (g1 < b1 ? 6 : 0)) / 6;
  else if (max === g1) h = ((b1 - r1) / d + 2) / 6;
  else h = ((r1 - g1) / d + 4) / 6;
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hslToRgbValues({ h, s, l }: HSL): RGB {
  const s1 = s / 100, l1 = l / 100;
  const c = (1 - Math.abs(2 * l1 - 1)) * s1;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l1 - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

// ─── WCAG 2.1 Contrast ─────────────────────────────────────────────────────

function relativeLuminance({ r, g, b }: RGB): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

export function contrastRatio(c1: RGB, c2: RGB): number {
  const l1 = relativeLuminance(c1), l2 = relativeLuminance(c2);
  const lighter = Math.max(l1, l2), darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function wcagLevel(ratio: number): WcagLevel {
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA Large';
  return 'Fail';
}

/**
 * Convenience: compute W/B contrast for a given hex color
 */
export function getWBContrast(hex: string): {
  contrastOnWhite: number;
  contrastOnBlack: number;
  wcagWhite: WcagLevel;
  wcagBlack: WcagLevel;
} {
  const rgb = hexToRgbValues(hex);
  const white: RGB = { r: 255, g: 255, b: 255 };
  const black: RGB = { r: 0, g: 0, b: 0 };
  const cw = contrastRatio(rgb, white);
  const cb = contrastRatio(rgb, black);
  return {
    contrastOnWhite: Math.round(cw * 100) / 100,
    contrastOnBlack: Math.round(cb * 100) / 100,
    wcagWhite: wcagLevel(cw),
    wcagBlack: wcagLevel(cb),
  };
}

// ─── Color Naming ───────────────────────────────────────────────────────────

function colorCategory(hsl: HSL): string {
  if (hsl.s < 10) {
    if (hsl.l < 15) return 'Black';
    if (hsl.l < 40) return 'Dark Gray';
    if (hsl.l < 60) return 'Gray';
    if (hsl.l < 85) return 'Light Gray';
    return 'White';
  }
  const names: [number, string][] = [
    [15, 'Red'], [40, 'Orange'], [65, 'Yellow'], [150, 'Green'],
    [195, 'Cyan'], [255, 'Blue'], [285, 'Purple'], [330, 'Pink'], [360, 'Red'],
  ];
  for (const [limit, name] of names) {
    if (hsl.h <= limit) {
      if (hsl.l < 25) return `Dark ${name}`;
      if (hsl.l > 75) return `Light ${name}`;
      return name;
    }
  }
  return 'Color';
}

// ─── K-Means Clustering ─────────────────────────────────────────────────────

function colorDistance(a: RGB, b: RGB): number {
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

function kMeans(
  pixels: RGB[],
  k: number,
  maxIter = 20,
  seed = 0,
): { center: RGB; count: number }[] {
  if (pixels.length === 0) return [];

  // Seeded pseudo-random for reproducible but varied initial centroids
  let rng = seed;
  const nextRand = () => {
    rng = (rng * 1664525 + 1013904223) & 0x7fffffff;
    return rng / 0x7fffffff;
  };

  // Init centroids using seed-based random selection
  const centroids: RGB[] = [];
  const usedIndices = new Set<number>();
  for (let i = 0; i < k; i++) {
    let idx: number;
    do {
      idx = Math.floor(nextRand() * pixels.length);
    } while (usedIndices.has(idx) && usedIndices.size < pixels.length);
    usedIndices.add(idx);
    centroids.push({ ...pixels[idx] });
  }

  const assignments = new Array(pixels.length).fill(0);
  for (let iter = 0; iter < maxIter; iter++) {
    let changed = false;
    for (let i = 0; i < pixels.length; i++) {
      let minDist = Infinity, best = 0;
      for (let j = 0; j < k; j++) {
        const d = colorDistance(pixels[i], centroids[j]);
        if (d < minDist) { minDist = d; best = j; }
      }
      if (assignments[i] !== best) { assignments[i] = best; changed = true; }
    }
    if (!changed) break;
    for (let j = 0; j < k; j++) {
      let sr = 0, sg = 0, sb = 0, count = 0;
      for (let i = 0; i < pixels.length; i++) {
        if (assignments[i] === j) {
          sr += pixels[i].r; sg += pixels[i].g; sb += pixels[i].b; count++;
        }
      }
      if (count > 0) {
        centroids[j] = {
          r: Math.round(sr / count),
          g: Math.round(sg / count),
          b: Math.round(sb / count),
        };
      }
    }
  }

  const counts = new Array(k).fill(0);
  assignments.forEach(a => counts[a]++);
  return centroids.map((center, i) => ({ center, count: counts[i] }));
}

// ─── Pixel Extraction ───────────────────────────────────────────────────────

export function extractPixels(img: HTMLImageElement, sampleSize = 10000): RGB[] {
  const canvas = document.createElement('canvas');
  const maxDim = 200;
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const pixels: RGB[] = [];
  const step = Math.max(1, Math.floor(data.length / 4 / sampleSize));
  for (let i = 0; i < data.length; i += step * 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a < 128) continue; // skip transparent pixels
    pixels.push({ r, g, b });
  }
  return pixels;
}

// ─── Build ColorInfo helper ─────────────────────────────────────────────────

function makeColorInfo(rgb: RGB, population: number): ExtractedColorInfo {
  const hsl = rgbToHslValues(rgb);
  const lightL = Math.min(95, hsl.l + 25);
  const darkL = Math.max(10, hsl.l - 25);
  return {
    hex: rgbToHex(rgb),
    rgb,
    hsl,
    name: colorCategory(hsl),
    population,
    light: rgbToHex(hslToRgbValues({ h: hsl.h, s: hsl.s, l: lightL })),
    dark: rgbToHex(hslToRgbValues({ h: hsl.h, s: hsl.s, l: darkL })),
  };
}

// ─── Main Palette Extraction ────────────────────────────────────────────────

export function extractPalette(
  img: HTMLImageElement,
  totalColors = 6,
  seed = 0,
): PaletteResult {
  const pixels = extractPixels(img);
  const clusters = kMeans(pixels, 16, 25, seed).sort((a, b) => b.count - a.count);
  const total = clusters.reduce((s, c) => s + c.count, 0);

  const all = clusters.map(c => ({
    rgb: c.center,
    hsl: rgbToHslValues(c.center),
    pop: Math.round((c.count / total) * 100),
  }));

  // 1. Find neutrals: lightest, darkest, most desaturated
  const byLightness = [...all].sort((a, b) => b.hsl.l - a.hsl.l);
  const lightest = byLightness[0];
  const darkest = byLightness[byLightness.length - 1];
  const bySaturation = [...all].sort((a, b) => a.hsl.s - b.hsl.s);
  const grayest = bySaturation.find(c => c !== lightest && c !== darkest) || bySaturation[0];

  const neutralSet = new Set([lightest, darkest, grayest]);
  const neutralArr = [...neutralSet];
  const neutralCount = Math.min(neutralArr.length, 3);
  const chromaticTarget = totalColors - neutralCount;

  const neutral: ExtractedColorInfo[] = neutralArr
    .slice(0, neutralCount)
    .map(c => makeColorInfo(c.rgb, c.pop));

  // 2. Remaining: sort by vibrancy score
  const remaining = all.filter(c => !neutralSet.has(c));
  const chromatic = remaining.sort((a, b) => {
    const scoreA = a.hsl.s * 2 + (50 - Math.abs(a.hsl.l - 50));
    const scoreB = b.hsl.s * 2 + (50 - Math.abs(b.hsl.l - 50));
    return scoreB - scoreA;
  });

  // Filter for diversity: skip colors too similar in hue
  const picked: typeof chromatic = [];
  for (const c of chromatic) {
    const tooSimilar = picked.some(p => {
      const hueDiff = Math.abs(c.hsl.h - p.hsl.h);
      const hueDistance = Math.min(hueDiff, 360 - hueDiff);
      const lDiff = Math.abs(c.hsl.l - p.hsl.l);
      return hueDistance < 25 && lDiff < 20;
    });
    if (!tooSimilar) picked.push(c);
    if (picked.length >= chromaticTarget) break;
  }

  // Fill remaining if needed
  if (picked.length < chromaticTarget) {
    for (const c of chromatic) {
      if (!picked.includes(c)) picked.push(c);
      if (picked.length >= chromaticTarget) break;
    }
  }

  const primary: ExtractedColorInfo[] = picked.slice(0, 2).map(c => makeColorInfo(c.rgb, c.pop));
  const accent: ExtractedColorInfo[] = picked.slice(2, chromaticTarget).map(c => makeColorInfo(c.rgb, c.pop));

  if (primary.length === 0 && all.length > 0) {
    primary.push(makeColorInfo(all[0].rgb, all[0].pop));
  }

  // Deduplicate by hex
  const seen = new Set<string>();
  const dedup = (arr: ExtractedColorInfo[]) => arr.filter(c => {
    if (seen.has(c.hex)) return false;
    seen.add(c.hex);
    return true;
  });

  return { primary: dedup(primary), accent: dedup(accent), neutral: dedup(neutral) };
}

// ─── Accessible Shade/Tint Generation ───────────────────────────────────────

/**
 * Generates a ramp of shades with WCAG contrast data attached.
 * Used for displaying W/B contrast on shade & tint rows.
 */
export function generateAccessibleShades(hex: string, count = 9): AccessibleShadeInfo[] {
  const rgb = hexToRgbValues(hex);
  const hsl = rgbToHslValues(rgb);
  const white: RGB = { r: 255, g: 255, b: 255 };
  const black: RGB = { r: 0, g: 0, b: 0 };
  const labels = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

  return Array.from({ length: count }, (_, i) => {
    const l = Math.round(95 - (i * (90 / (count - 1))));
    const shadeRgb = hslToRgbValues({ h: hsl.h, s: hsl.s, l });
    const shadeHex = rgbToHex(shadeRgb);
    const cw = contrastRatio(shadeRgb, white);
    const cb = contrastRatio(shadeRgb, black);
    return {
      hex: shadeHex,
      label: labels[i] || `${(i + 1) * 100}`,
      contrastOnWhite: Math.round(cw * 100) / 100,
      contrastOnBlack: Math.round(cb * 100) / 100,
      wcagWhite: wcagLevel(cw),
      wcagBlack: wcagLevel(cb),
    };
  });
}
